import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
  useSearchUnsplashQuery,
  SelectCoverQuery,
} from '@trello/graphql/generated';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
} from 'app/common/lib/util/preview';
import { SearchResultImageButton } from './SearchResultImageButton';
import { forTemplate } from 'app/common/lib/util/i18n';

import styles from './SelectUnsplashCover.less';

const format = forTemplate('card_cover_chooser');

interface SelectUnsplashCoverProps {
  cardId: string;
  navigateToAddCover: () => void;
}

export const SelectUnsplashCover: React.FunctionComponent<SelectUnsplashCoverProps> = ({
  cardId,
  navigateToAddCover,
}) => {
  const [selectedTopPhotoId, setSelectedTopPhotoId] = useState<string | null>(
    null,
  );

  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });

  const { data: topPhotosData } = useSearchUnsplashQuery({
    variables: {
      query: '',
      perPage: 9,
      page: 1,
    },
    fetchPolicy: 'cache-only',
  });

  const [updateCardCover] = useUpdateCardCoverMutation();

  const setCover = async (
    cover: {
      idUploadedBackground?: string;
    } | null,
  ) => {
    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
    });
  };

  if (!data?.card) {
    throw new Error(
      'Failed to load card details from cache for SelectUnsplashCover.',
    );
  }

  const card = data && data.card;
  const currentCover = card ? card.cover : null;

  const [lastUploadedBackgroundCover] = useState<
    NonNullable<SelectCoverQuery['card']>['cover']
  >(currentCover);

  useEffect(() => {
    const hasUploadedBackgroundCover = currentCover?.idUploadedBackground;
    const hasColorOrAttachmentCover =
      currentCover?.color || currentCover?.idAttachment;
    if (!hasUploadedBackgroundCover || hasColorOrAttachmentCover) {
      setSelectedTopPhotoId(null);
    }
  }, [currentCover, lastUploadedBackgroundCover]);

  let firstUnsplashCoverNode: React.ReactNode = null;
  let numTopPhotosToShow = 6;

  if (
    lastUploadedBackgroundCover &&
    lastUploadedBackgroundCover.idUploadedBackground &&
    lastUploadedBackgroundCover.scaled
  ) {
    const preview =
      smallestPreviewBiggerThan(lastUploadedBackgroundCover.scaled, 256) ||
      biggestPreview(lastUploadedBackgroundCover.scaled);

    if (preview && preview.url) {
      firstUnsplashCoverNode = (
        <button
          key="firstUnsplashCoverNode"
          className={cx('button', styles.unsplashTilesItem, {
            [styles.selected]:
              currentCover &&
              lastUploadedBackgroundCover.idUploadedBackground ===
                currentCover.idUploadedBackground,
          })}
          style={{
            backgroundColor:
              lastUploadedBackgroundCover?.edgeColor || 'transparent',
            backgroundImage: `url('${preview.url}')`,
            backgroundSize: 'cover',
          }}
          onClick={() => {
            if (
              currentCover &&
              lastUploadedBackgroundCover.idUploadedBackground ===
                currentCover.idUploadedBackground
            ) {
              setCover(null);
              setSelectedTopPhotoId(null);
            } else if (lastUploadedBackgroundCover?.idUploadedBackground) {
              setCover({
                idUploadedBackground:
                  lastUploadedBackgroundCover.idUploadedBackground,
              });
              setSelectedTopPhotoId(null);
            }
          }}
        />
      );

      numTopPhotosToShow = 5;
    }
  }

  const potentialUnsplashCovers = [];

  if (topPhotosData?.unsplashPhotos?.length) {
    let index = 0;
    for (const unsplashPhoto of topPhotosData.unsplashPhotos.slice(
      0,
      numTopPhotosToShow,
    )) {
      const { urls, links, user, id } = unsplashPhoto;
      potentialUnsplashCovers.unshift(
        <SearchResultImageButton
          key={id}
          urls={urls}
          links={links}
          user={user}
          index={index++}
          cardId={cardId}
          onSetCover={() => {
            setSelectedTopPhotoId(id);
          }}
          onSetCoverFailed={() => {}}
          buttonClassName={cx(styles.unsplashTilesItem, {
            [styles.selected]: selectedTopPhotoId === id,
          })}
          onClick={(e) => {
            if (selectedTopPhotoId === id) {
              e.preventDefault();
              setCover(null);
            }

            return;
          }}
        />,
      );
    }
  }

  return (
    <>
      <h4 className={styles.heading}>{format('unsplash')}</h4>
      <div className={styles.unsplashTiles}>
        {firstUnsplashCoverNode}
        {potentialUnsplashCovers}
      </div>
      <button
        className={cx('button', styles.searchButton)}
        onClick={navigateToAddCover}
      >
        {format('search-for-photos')}
      </button>
    </>
  );
};
